import React, { useEffect, useState } from 'react';
import Modal, { ModalTitle, ModalActions } from '../../components/Modal/Modal';
import Button from '../../components/Button/Button';
import { PortalField } from '../../components/FormField/PortalField';
import { PortalInput } from '../../components/FormField/PortalInput';
import { PortalSelect } from '../../components/FormField/PortalSelect';
import { FormGrid } from '../../components/FormField/FormGrid';
import { US_STATE_OPTIONS } from '../../lib/utils';
import { AccountAddress, RescueAccountView, contactInfoFromView, toContactInfoInput } from '../../lib/accountAdapters';
import { useAccountSave } from './useAccountSave';
import styles from './Account.module.scss';

// The org address doubles as a shipping origin for Nano boxes; our 3PL caps
// address fields at 40 characters (same rule as the legacy /vet/account).
const ADDRESS_LINE_MAX_LENGTH = 40;
const ADDRESS_LINE_MAX_ERROR = 'Max 40 characters allowed';

export type AddressVariant = 'address' | 'shipping_address';

const VARIANT_COPY: Record<AddressVariant, { title: string; desc: string; success: string }> = {
  address: {
    title: 'Edit Address',
    desc: "Your organization's main contact address.",
    success: 'Address updated',
  },
  shipping_address: {
    title: 'Edit Shipping Address',
    desc: 'Where device orders and Fi Nano microchips ship. You can still change this per order at checkout.',
    success: 'Shipping address updated',
  },
};

interface EditAddressModalProps {
  open: boolean;
  onClose: () => void;
  view: RescueAccountView;
  variant: AddressVariant;
}

export default function EditAddressModal({ open, onClose, view, variant }: EditAddressModalProps) {
  const { save, saving } = useAccountSave(variant);
  const copy = VARIANT_COPY[variant];
  const initial: AccountAddress | null = variant === 'address' ? view.address : view.shippingAddress;

  const [line1, setLine1] = useState('');
  const [line2, setLine2] = useState('');
  const [city, setCity] = useState('');
  const [usState, setUsState] = useState('');
  const [zip, setZip] = useState('');
  const [showErrors, setShowErrors] = useState(false);

  useEffect(() => {
    if (open) {
      setLine1(initial?.line1 ?? '');
      setLine2(initial?.line2 ?? '');
      setCity(initial?.city ?? '');
      setUsState(initial?.state ?? '');
      setZip(initial?.zip ?? '');
      setShowErrors(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- seed once per open cycle
  }, [open]);

  const line1Error = !line1.trim()
    ? 'Please enter an address'
    : line1.trim().length > ADDRESS_LINE_MAX_LENGTH
      ? ADDRESS_LINE_MAX_ERROR
      : undefined;
  const line2Error = line2.trim().length > ADDRESS_LINE_MAX_LENGTH ? ADDRESS_LINE_MAX_ERROR : undefined;
  const cityError = !city.trim()
    ? 'Please enter a city'
    : city.trim().length > ADDRESS_LINE_MAX_LENGTH
      ? ADDRESS_LINE_MAX_ERROR
      : undefined;
  const stateError = usState ? undefined : 'Please select a state';
  const zipError = zip.trim() ? undefined : 'Please enter a zip code';

  async function handleSave() {
    if (line1Error || line2Error || cityError || stateError || zipError) {
      setShowErrors(true);
      return;
    }
    if (!view.resellerId) {
      return;
    }

    const edited: AccountAddress = {
      line1: line1.trim(),
      line2: line2.trim(),
      city: city.trim(),
      state: usState,
      zip: zip.trim(),
    };

    const input =
      variant === 'address'
        ? {
            id: view.resellerId,
            contactInfo: toContactInfoInput({ ...contactInfoFromView(view), ...edited }),
          }
        : {
            id: view.resellerId,
            shippingAddress: {
              line1: edited.line1,
              line2: edited.line2 || undefined,
              city: edited.city,
              state: edited.state,
              zip: edited.zip,
            },
          };

    const ok = await save(input, copy.success);
    if (ok) {
      onClose();
    }
  }

  return (
    <Modal open={open} onClose={onClose}>
      <ModalTitle>{copy.title}</ModalTitle>
      <p className={styles.modalDesc}>{copy.desc}</p>

      <PortalField label="Address line 1" error={line1Error} showError={showErrors && !!line1Error}>
        <PortalInput
          value={line1}
          onChange={(e) => setLine1(e.target.value)}
          placeholder="Street address"
          hasError={showErrors && !!line1Error}
        />
      </PortalField>

      <PortalField label="Address line 2 (optional)" error={line2Error} showError={showErrors && !!line2Error}>
        <PortalInput
          value={line2}
          onChange={(e) => setLine2(e.target.value)}
          placeholder="Suite, unit, loading dock…"
          hasError={showErrors && !!line2Error}
        />
      </PortalField>

      <PortalField label="City" error={cityError} showError={showErrors && !!cityError}>
        <PortalInput
          value={city}
          onChange={(e) => setCity(e.target.value)}
          placeholder="City"
          hasError={showErrors && !!cityError}
        />
      </PortalField>

      <FormGrid>
        <PortalField label="State" error={stateError} showError={showErrors && !!stateError}>
          <PortalSelect
            value={usState}
            onChange={(e) => setUsState(e.target.value)}
            hasError={showErrors && !!stateError}
          >
            <option value="">Select state</option>
            {US_STATE_OPTIONS.map((s) => (
              <option key={s.abbreviation} value={s.abbreviation}>
                {s.name}
              </option>
            ))}
          </PortalSelect>
        </PortalField>
        <PortalField label="Zip" error={zipError} showError={showErrors && !!zipError}>
          <PortalInput
            value={zip}
            onChange={(e) => setZip(e.target.value)}
            placeholder="Zip code"
            autoComplete="postal-code"
            hasError={showErrors && !!zipError}
          />
        </PortalField>
      </FormGrid>

      <ModalActions>
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button variant="green" onClick={handleSave} disabled={saving}>
          {saving ? 'Saving…' : 'Save changes'}
        </Button>
      </ModalActions>
    </Modal>
  );
}
